import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface AddVersionFormProps {
  pricingTableId: string;
  onVersionAdded: () => void;
}

export function AddVersionForm({ pricingTableId, onVersionAdded }: AddVersionFormProps) {
  const [versionName, setVersionName] = useState('');
  const [price, setPrice] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError(null);

    try {
      const response = await fetch(`/api/pricing-tables/${pricingTableId}/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          versionName,
          price: parseFloat(price),
          productDescription,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add version');
      }

      const result = await response.json();

      if (result.success) {
        setVersionName('');
        setPrice('');
        setProductDescription('');
        onVersionAdded();
        alert(`Version '${versionName}' added successfully.`);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to add version. Please try again.';
      setFormError(errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle>Add New Version</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor='versionName'>Version Name</Label>
            <Input
              id='versionName'
              value={versionName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVersionName(e.target.value)}
              maxLength={100}
              disabled={isLoading}
              placeholder="e.g., Basic Plan"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor='price'>Price ($)</Label>
            <Input
              type='number'
              id='price'
              value={price}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrice(e.target.value)}
              step='0.01'
              min='0'
              disabled={isLoading}
              placeholder="e.g., 9.99"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor='productDescription'>Product Description</Label>
            <Textarea
              id='productDescription'
              value={productDescription}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setProductDescription(e.target.value)}
              maxLength={500}
              disabled={isLoading}
              placeholder="Describe the features included in this version..."
              rows={4}
              required
            />
          </div>
          {formError && <p className="text-sm text-red-600">Error: {formError}</p>}
          <Button type='submit' disabled={isLoading}>
            {isLoading ? 'Adding...' : 'Add Version'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
